import { useEffect } from "react";
import { SendHorizontal } from "lucide-react";
import { connect } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";
import { withRouter, type RouteComponentProps } from "react-router-dom";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { WFPButton } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

import {
  listCommodities,
  listUnitsOfMeasure,
  addMarketOffer,
  addMarketOfferReset,
  listTraders,
  listExchangeLocations,
  listExchangeLocationTypes,
  treeAdministrativeAreas,
  API_URL_FARMER_DETAILS,
} from "../../actions";
import {
  getErrors,
  getStatusErrorMessage,
  valueOrDefault,
  formatNumber,
  getCountryObjectFromCache,
} from "../../utils";
import { adaptUnitsOfMeasure, adaptTrader, adaptAdministrativeAreas } from "../../utils/adapters";
import { toDeliveryCostPayload } from "../../utils/delivery-cost";
import client from "../../client";
import { Deposit } from "../Deposits/DepositCard/deposit-dtos";
import {
  OfferForm,
  type Trader,
  type CustomExchangeLocation,
  type ExchangeLocationType,
  type AdministrativeAreaOption,
} from "./OfferForm";
import { createOfferSchema, type OfferFormValues } from "./schema";

export const isDeposit = (depositOrCommodity: OfferSource) => !!depositOrCommodity.id;

// Superset of deposit + legacy commodity shapes accepted by the payload adapter.
type OfferSource = Deposit & {
  commodity_id?: number;
  unit_of_measure_id?: number;
  aggregator_id?: number;
};

// Adapts values entered in the form to a payload accepted by the server.
// Logic ported verbatim from the legacy redux-form container.
export const adaptDirectOfferFromValues = (
  formValues: OfferFormValues,
  depositOrCommodity: OfferSource,
  _unitsOfMeasure: unknown,
  commodityId?: number | null
) => {
  const quantity =
    valueOrDefault(formValues.minimum_quantity, undefined) || depositOrCommodity.quantity;

  const has_custom_exchange_location =
    typeof (formValues.delivery_location && formValues.delivery_location.value) === "object";

  return {
    buy_commodity: isDeposit(depositOrCommodity)
      ? (depositOrCommodity.commodity && depositOrCommodity.commodity.id) ||
        commodityId ||
        (depositOrCommodity.farmer_produce &&
          depositOrCommodity.farmer_produce.commodity &&
          depositOrCommodity.farmer_produce.commodity.id)
      : depositOrCommodity.commodity_id,
    unit_of_measure: isDeposit(depositOrCommodity)
      ? depositOrCommodity.unit_of_measure.id
      : depositOrCommodity.unit_of_measure_id,
    quality: valueOrDefault(depositOrCommodity.quality, undefined),
    delivery_location: has_custom_exchange_location
      ? (formValues.delivery_location!.value as { deliveryLocationType: string })
          .deliveryLocationType
      : valueOrDefault(
          formValues.delivery_location && formValues.delivery_location.value,
          undefined
        ),
    tagged_aggregator: isDeposit(depositOrCommodity)
      ? depositOrCommodity.aggregator.id
      : depositOrCommodity.aggregator_id,
    negotiable: valueOrDefault(formValues.negotiable && formValues.negotiable.value, undefined),
    price: valueOrDefault(formValues.price, undefined) || depositOrCommodity.price,
    delivery_cost: toDeliveryCostPayload(
      formValues.delivery_location?.value,
      formValues.delivery_cost
    ),
    minimum_quantity: quantity,
    maximum_quantity: quantity,
    valid_until: undefined,
    contract_number: valueOrDefault(formValues.contract_number, undefined),
    offer_type: "DIRECT_OFFER",
    deposit: valueOrDefault(depositOrCommodity.id, -1 as unknown as undefined),
    planned_exchange_time: formValues.exchange_date_direct
      ? formValues.exchange_date_direct.toISOString()
      : undefined,
    comment: valueOrDefault(formValues.comment, undefined),
    custom_delivery_location: has_custom_exchange_location
      ? (formValues.delivery_location!.value as { custom_delivery_location: number })
          .custom_delivery_location
      : undefined,
    custom_delivery_location_type: has_custom_exchange_location
      ? (formValues.delivery_location!.value as { custom_delivery_location_type: number })
          .custom_delivery_location_type
      : undefined,
  };
};

interface OwnProps {
  deposit?: Deposit;
  isOpen: boolean;
  onCancel: () => void;
  redirectPath?: string;
}

interface StateProps {
  isFetching: boolean;
  errorMessage: string;
  unitsOfMeasure: unknown[];
  administrativeAreas: AdministrativeAreaOption[];
  customExchangeLocations: CustomExchangeLocation[];
  exchangeLocationTypes: ExchangeLocationType[];
  fieldErrors: unknown;
  trader: Trader;
}

interface DispatchProps {
  treeAdministrativeAreas: (countryId: number) => void;
  listExchangeLocations: () => void;
  listExchangeLocationTypes: () => void;
  listCommodities: () => void;
  listUnitsOfMeasure: () => void;
  addMarketOffer: (data: ReturnType<typeof adaptDirectOfferFromValues>) => Promise<unknown>;
  addMarketOfferReset: () => void;
  listTraders: () => void;
}

interface FetchSlice {
  isFetching: boolean;
  errorMessage: string;
}

interface RootState {
  listCommodities: FetchSlice;
  listUnitsOfMeasure: FetchSlice & { data: { results: unknown[] } };
  listTraders: FetchSlice & { data: { results: unknown[] } };
  addMarketOffer: { isLoading: boolean; error: unknown };
  treeAdministrativeAreas: { data: { administrative_areas: unknown } };
  listExchangeLocations: { data: { results: CustomExchangeLocation[] } };
  listExchangeLocationTypes: { data: { results: ExchangeLocationType[] } };
}

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = <TResult,>(dispatch: AppDispatch, action: unknown): TResult =>
  dispatch(action as UnknownAction) as TResult;

type Props = OwnProps & StateProps & DispatchProps & RouteComponentProps;

const emptyValues: OfferFormValues = {
  minimum_quantity: "",
  price: "",
  negotiable: null,
  exchange_date_direct: null,
  delivery_location: null,
  delivery_cost: "",
  comment: "",
  contract_number: "",
};

export const DirectOfferDetails = ({
  treeAdministrativeAreas,
  administrativeAreas,
  isFetching,
  errorMessage,
  listCommodities,
  listUnitsOfMeasure,
  unitsOfMeasure,
  fieldErrors,
  addMarketOffer,
  addMarketOfferReset,
  history,
  trader,
  listTraders,
  deposit,
  isOpen,
  onCancel,
  listExchangeLocations,
  listExchangeLocationTypes,
  customExchangeLocations,
  exchangeLocationTypes,
  redirectPath = "/representative/offer",
}: Props) => {
  const { t } = useTranslation();

  const methods = useForm<OfferFormValues>({
    mode: "onChange",
    defaultValues: emptyValues,
    resolver: zodResolver(
      createOfferSchema(Number(deposit?.quantity ?? 0), t)
    ) as unknown as Resolver<OfferFormValues>,
  });
  const {
    handleSubmit,
    reset,
    formState: { isValid },
  } = methods;

  useEffect(() => {
    listCommodities();
    listUnitsOfMeasure();
    listTraders();
    listExchangeLocations();
    listExchangeLocationTypes();

    const country = getCountryObjectFromCache();
    if (country) {
      treeAdministrativeAreas(country.id);
    }
    return () => {
      addMarketOfferReset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reinitialize the form whenever a different deposit is opened.
  useEffect(() => {
    if (deposit) {
      reset({
        ...emptyValues,
        minimum_quantity: formatNumber(deposit.quantity, false),
        price: formatNumber(deposit.price, false),
      });
    }
  }, [deposit, reset]);

  const handleClose = () => {
    reset(emptyValues);
    addMarketOfferReset();
    onCancel();
  };

  const onSubmit = handleSubmit(async values => {
    if (!deposit) {
      return;
    }
    try {
      let commodityId: number | null = null;

      if (!deposit.commodity) {
        const farmerId = deposit.farmer?.person?.uuid;
        if (farmerId) {
          const response = await client.get(API_URL_FARMER_DETAILS(farmerId));
          const produce = response.data?.produce;
          if (Array.isArray(produce)) {
            // ponytail: legacy compares produce uuid to farmer_produce; kept verbatim.
            const matched = produce.find(
              item => item.uuid === (deposit.farmer_produce as unknown as string)
            );
            commodityId = matched?.commodity?.id ?? null;
          }
        }
      }

      const offerData = adaptDirectOfferFromValues(
        values,
        deposit as OfferSource,
        unitsOfMeasure,
        commodityId
      );

      const result = await addMarketOffer(offerData);
      if (result) {
        history.push(redirectPath);
      }
    } catch (error) {
      console.error("Failed to validate direct offer:", error);
    }
  });

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t("directOfferDetails.form.offerProposal", {
              aggregator: deposit?.aggregator?.name || "",
            })}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("directOfferDetails.form.sendOffer")}
          </DialogDescription>
        </DialogHeader>

        {deposit && (
          <OfferForm
            methods={methods}
            deposit={deposit}
            trader={trader}
            customExchangeLocations={customExchangeLocations}
            administrativeAreas={administrativeAreas}
            exchangeLocationTypes={exchangeLocationTypes}
            fieldErrors={fieldErrors}
            t={t}
          />
        )}

        {errorMessage && <p className="text-destructive text-sm">{errorMessage}</p>}
        {getErrors(fieldErrors, "deposit") && (
          <p className="text-destructive text-sm">{getErrors(fieldErrors, "deposit")}</p>
        )}

        <div className="flex justify-end gap-2">
          <WFPButton variant="secondary" onClick={handleClose}>
            {t("common.cancel")}
          </WFPButton>
          <WFPButton onClick={onSubmit} disabled={!isValid || isFetching}>
            {isFetching ? <Spinner className="size-4" /> : <SendHorizontal />}
            {isFetching ? t("directOfferDetails.form.sending") : t("common.send")}
          </WFPButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const mapStateToProps = (state: RootState): StateProps => ({
  isFetching:
    state.listCommodities.isFetching ||
    state.listUnitsOfMeasure.isFetching ||
    state.listTraders.isFetching ||
    state.addMarketOffer.isLoading,
  errorMessage:
    state.listCommodities.errorMessage ||
    state.listUnitsOfMeasure.errorMessage ||
    state.listTraders.errorMessage ||
    getStatusErrorMessage(state.addMarketOffer.error) ||
    "",
  unitsOfMeasure: adaptUnitsOfMeasure(state.listUnitsOfMeasure.data.results),
  administrativeAreas: adaptAdministrativeAreas(
    state.treeAdministrativeAreas.data.administrative_areas
  ),
  customExchangeLocations: state.listExchangeLocations.data.results,
  exchangeLocationTypes: state.listExchangeLocationTypes.data.results,
  fieldErrors: state.addMarketOffer.error,
  trader: adaptTrader(state.listTraders.data.results),
});

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  treeAdministrativeAreas: countryId =>
    dispatchAction(dispatch, treeAdministrativeAreas({}, `${countryId}/administrative_areas/`)),
  listExchangeLocations: () => dispatchAction(dispatch, listExchangeLocations()),
  listExchangeLocationTypes: () => dispatchAction(dispatch, listExchangeLocationTypes()),
  listCommodities: () => dispatchAction(dispatch, listCommodities()),
  listUnitsOfMeasure: () => dispatchAction(dispatch, listUnitsOfMeasure()),
  addMarketOffer: data => dispatchAction<Promise<unknown>>(dispatch, addMarketOffer(data)),
  addMarketOfferReset: () => dispatchAction(dispatch, addMarketOfferReset()),
  listTraders: () => dispatchAction(dispatch, listTraders()),
});

export const ConnectedDirectOfferDetails = connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(DirectOfferDetails));
